import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { PayloadError } from "relay-runtime";

import {
  getMutationErrorMessage,
  useMutationWithError,
  WhooshMutationError,
} from "@/utils/hooks/useMutationWithError";

import {
  AppliedPromoCodeAnalyticsData,
  getAppliedPromoCodeAnalyticsData,
} from "../promoCodeAnalytics";
import {
  ApplyPromoCodeToBookingInput,
  useApplyPromoCodeToBookingMutation,
} from "./__generated__/useApplyPromoCodeToBookingMutation.graphql";

const invalidPromoCodeMessage = () =>
  t`That code is not valid for this booking.`;

const isNotFoundPayloadError = (error: PayloadError) => {
  if (!("extensions" in error)) {
    return false;
  }

  const extensions = error.extensions;

  return (
    typeof extensions === "object" &&
    extensions !== null &&
    "code" in extensions &&
    extensions.code === "NOT_FOUND"
  );
};

const getPayloadErrorMessage = (errors: PayloadError[]) => {
  const promoCodeWasNotFound = errors.some(isNotFoundPayloadError);

  if (promoCodeWasNotFound) {
    return invalidPromoCodeMessage();
  }

  return errors[0]?.message ?? t`An unknown error occurred. Please try again.`;
};

const getValidationErrorMessage = (errors: WhooshMutationError[]) => {
  const firstError = errors[0];

  return firstError
    ? getMutationErrorMessage(firstError)
    : t`An unknown error occurred. Please try again.`;
};

export const useApplyPromoCodeToBooking = () => {
  const [applyPromoCodeMutation, isApplyingPromoCode] =
    useMutationWithError<useApplyPromoCodeToBookingMutation>(
      useApplyPromoCodeToBookingMutationString
    );

  const applyPromoCodeToBooking = (
    input: ApplyPromoCodeToBookingInput,
    onCompleted: (analyticsData: AppliedPromoCodeAnalyticsData | null) => void,
    onValidationError: (message: string) => void
  ) => {
    applyPromoCodeMutation({
      variables: { input },
      onCompleted: (response) => {
        onCompleted(
          getAppliedPromoCodeAnalyticsData(response.responsePayload?.booking)
        );
      },
      onCompletedErrorCustom: (errors) => {
        onValidationError(getValidationErrorMessage(errors));
        return [];
      },
      onPayloadError: (errors) => {
        onValidationError(getPayloadErrorMessage(errors));
      },
    });
  };

  return [applyPromoCodeToBooking, isApplyingPromoCode] as const;
};

export const useApplyPromoCodeToBookingMutationString = graphql`
  mutation useApplyPromoCodeToBookingMutation(
    $input: ApplyPromoCodeToBookingInput!
  ) {
    responsePayload: applyPromoCodeToBooking(input: $input) {
      success
      errors {
        field
        code
        message
      }
      booking {
        id
        ...promoCodeAnalytics_booking
        ...useGetSummaryCalculations_Booking
        appliedPromoCode {
          id
          code
          discountType
          discountAmount
          calculationType
        }
      }
    }
  }
`;
